/**
 * How Affily works, as Mermaid charts for the admin area (/admin/diagrams).
 *
 * Drawn from the code, and kept here so a change to a flow edits its chart in
 * the same diff. If you change what one of these describes, change the chart.
 * The amounts are the templates' starting values (shared/src/templates.js)
 * and the default fees (shared/src/constants.js).
 */

const AFFILY = 'classDef affily fill:#eaf0fd,stroke:#2b5fd9,color:#1f2430';
const LATER = 'classDef later stroke-dasharray:5 4,color:#5f6573';

const systemMap = {
  key: 'system',
  title: 'System map',
  intro:
    'Who uses Affily, what it talks to, and what runs on its own. Every outside service is off until its setting is filled in on the server; whatever is off simply does not appear.',
  charts: [
    {
      title: 'Who talks to what',
      chart: `flowchart LR
  Brand["Brand<br/>runs programs, pays for results"]
  Creator["Creator<br/>joins, shares links, gets paid"]
  Shopper["Shopper<br/>never signs in"]
  Agent["AI agent<br/>API key, /api/v1 only"]
  subgraph Affily
    Web["Website and API"]
    Track["Tracking links<br/>affily.me/go/…"]
    Attr["Attribution<br/>sale → creator"]
    DB[("MongoDB")]
    Jobs["Scheduled jobs"]
  end
  Store["Brand's site or store<br/>tag, postback,<br/>Stripe, Shopify, WooCommerce"]
  Stripe["Affily's Stripe<br/>brands' cards, creators' payouts"]
  Social["TikTok, Instagram,<br/>YouTube, Facebook Pages"]
  SignIn["Google, Facebook,<br/>Instagram sign-in"]
  Mail["Email (SMTP)<br/>uploads (R2)"]
  Brand <--> Web
  Creator <--> Web
  Agent -->|"drafts only"| Web
  Shopper -->|"clicks a link"| Track
  Track -->|"redirects"| Store
  Store -->|"reports sales"| Attr
  Attr --> DB
  Web --> DB
  Web -->|"charges brand, pays creator"| Stripe
  Jobs -->|"reads followers and videos"| Social
  SignIn -->|"who is signing in"| Web
  Web --> Mail
  class Web,Track,Attr,DB,Jobs affily
  ${AFFILY}`,
      notes:
        'Affily never posts to a social account and never stores card numbers: Stripe holds the cards, and the platforms are only read, through the creator’s own connection.',
    },
    {
      title: 'A program’s life',
      chart: `flowchart LR
  Draft["Draft<br/>invisible, page 404s"] -->|"brand publishes,<br/>email confirmed"| Intake
  Intake{"Applications"} -->|"rolling"| Running
  Intake -->|"window, before the start"| Running
  Running["Running<br/>clicks and actions credited"] --> End{"End date"}
  End -->|"renewal on (default)"| Renew["Renews for a year"] --> Running
  End -->|"renewal off"| Ended["Ended<br/>nothing new credited"]
  Ended -->|"pays after the end on"| Late["Renewals and recorded sales<br/>from before keep paying"]`,
      notes:
        'Once a creator has applied the currency and what the program pays for are fixed; once one has joined, the end can only move later and paying after the end can be switched on but not off. A click outside the dates still lands on the brand’s site but credits nobody.',
    },
    {
      title: 'From a click to a payout',
      chart: `flowchart LR
  A["Creator shares<br/>link or code"] --> B["Shopper clicks<br/>or types the code"]
  B --> C["Affily records the click,<br/>redirects with aff_click"]
  C --> D["Shopper buys or books<br/>on the brand's site"]
  D --> E["Site or store<br/>reports it"]
  M["Brand records it by hand<br/>e.g. a house sale"] -.-> F
  E --> F{"Creator found?"}
  F -->|"yes"| G["Commission approved<br/>at the frozen terms"]
  F -->|"no"| X["Recorded, not paid,<br/>with the reason"]
  G --> H["Brand settles<br/>confirms it's real"]
  H --> I["Brand pays<br/>card, or bank transfer from €1,000"]
  I --> J["Stripe transfers the<br/>creator's share"]
  class C,F,G affily
  ${AFFILY}`,
      notes:
        'Commission states: approved → settled → paid. "Recorded, not paid" always carries its reason: outside the dates, a different currency, an action the program only tracks, or no creator found.',
    },
    {
      title: 'What runs on its own',
      chart: `flowchart TB
  Monitor["Health monitor · every 5 min<br/>fake click and sale end to end,<br/>database, deliveries, sync, renewals"] -->|"failing or degraded"| Alert["Email to ALERT_EMAIL<br/>at most every 6 h"]
  Monitor --> Health["/admin/health"]
  Social["Social sync · every 5 min tick"] --> Videos["Competition videos<br/>every 3 h, every 15 min near a first-to number,<br/>last read at the end (YouTube 3 days later)"]
  Social --> Followers["Creators' followers<br/>once a day"]
  Renewals["Program renewals · hourly"] --> Year["A renewing program's<br/>end moves on a year"]`,
      notes: 'The monitor and renewals take a lease in Mongo first, so only one server runs them at a time.',
    },
  ],
};

const programFlows = {
  key: 'programs',
  title: 'Program flows',
  intro:
    'One chart per template a brand can start from, with its starting values. Affily’s fee is split down the middle: half comes out of the creator’s commission, half is added to what the brand pays. Digital products 10.5%, everything else 8%, competition prizes 11%. A card payment adds the card fee for the brand; a bank transfer (€1,000 or more, euros) adds none.',
  charts: [
    {
      title: 'Software subscriptions · digital, fee 10.5%',
      chart: `flowchart LR
  A["Creator joins:<br/>terms frozen, link"] --> B["Visitor clicks<br/>60-day cookie"]
  B --> C{"What do they do?"}
  C -->|"free account"| D["€1.50<br/>once per visitor"]
  C -->|"paid plan"| E["20% of the payment"]
  E --> F["Each renewal, cycles 2–12:<br/>another 20%"]
  D --> S["Settle and pay"]
  E --> S
  F --> S`,
      notes: 'Renewals carry no click: they are credited to whoever brought the subscription’s first payment.',
    },
    {
      title: 'Online store · fee 8% (10.5% if digital)',
      chart: `flowchart LR
  A["Creator joins:<br/>link and discount code"] --> B["Shopper clicks the link<br/>or types the code"]
  B --> C["Store reports the order"]
  C --> D{"Creator found?<br/>code, then click, then device"}
  D -->|"yes"| E["15% of the order<br/>30-day cookie"]
  D -->|"no"| X["Recorded, not paid"]
  E --> R{"Refunded or cancelled?"}
  R -->|"yes"| Y["Brand reverses it<br/>before paying, with a reason"]
  R -->|"no"| S["Settle and pay"]`,
    },
    {
      title: 'Events and tickets · fee 8%',
      chart: `flowchart LR
  A["Creator joins"] --> B["Visitor clicks<br/>30-day cookie"] --> C["Buys a ticket"] --> D["20% of each ticket"] --> S["Settle and pay"]`,
    },
    {
      title: 'Leads · fee 8%',
      chart: `flowchart LR
  A["Creator joins"] --> B["Visitor clicks<br/>30-day cookie"] --> C["Fills in a form:<br/>site reports a lead"]
  C --> D{"First lead from<br/>this visitor?"}
  D -->|"yes"| E["€20"] --> S["Settle and pay"]
  D -->|"no"| X["Counted, not paid"]`,
    },
    {
      title: 'Pay per post · fee 8%',
      chart: `flowchart LR
  A["Creator joins"] --> B["Makes the post"] --> C["Submits its link"]
  C --> D{"Brand decides"}
  D -->|"approves"| E["€350 commission<br/>at the frozen amount"] --> S["Settle and pay"]
  D -->|"declines"| X["Nothing paid"]
  B --> T["Clicks and sales from the link:<br/>tracked, not paid"]`,
      notes: 'Approving needs a person signed in, never an API key, and a double-clicked Approve pays once.',
    },
    {
      title: 'Gifting · no money moves, no fee',
      chart: `flowchart LR
  A["Creator applies and asks<br/>for the gift, with an address"] --> B{"Brand decides"}
  B -->|"accepts"| C["Gift approved,<br/>stock goes down (50 to start)"]
  B -->|"declines"| X["Nothing sent"]
  C --> D["Brand ships it<br/>or books the stay"] --> E["Creator posts"]
  E --> F["Clicks and sales:<br/>tracked so the brand sees what it drove"]`,
      notes: 'The address is read on Affily after approving, never emailed, and deleted automatically afterwards.',
    },
    {
      title: 'Video competition · prizes 11%, sale bonus 8%',
      chart: `flowchart TB
  A["Runs 3 months, no renewal"] --> B["Creators join and connect<br/>TikTok, Instagram or YouTube"]
  B --> V["Post and enter videos"]
  B --> K["Visitors book viewings<br/>through creators' links"]
  V --> R["Affily re-reads the numbers"]
  R --> E["Competition ends: last read"]
  K --> E
  E --> P["Brand awards the prizes:<br/>most viewed €500, first to 100,000 €250,<br/>most shared €250, most bookings €350"]
  P --> Q["Each prize paid like a commission"]
  K --> S{"A booking<br/>becomes a sale"}
  S -->|"brand records it"| T["€1,000 bonus to the creator<br/>who brought the booking"]`,
      notes:
        '"First to 100,000" can be awarded as soon as a read sees it. Bookings count for the prize but don’t pay by themselves; the sale is brand-recorded only, so it can’t be faked from a tag.',
    },
  ],
};

const connections = {
  key: 'tracking',
  title: 'Connections & tracking',
  intro:
    'How a creator’s link becomes a credited sale: the click, the ways a brand reports sales, and how a sale from a connected store finds its program.',
  charts: [
    {
      title: 'A creator’s link: affily.me/go/…',
      chart: `flowchart TB
  A["Shopper opens affily.me/go/code"] --> B{"Link known<br/>and active?"}
  B -->|"no"| X["→ Affily's programs page"]
  B -->|"yes"| C{"Partnership active?"}
  C -->|"no"| Y["→ brand's site,<br/>nothing recorded"]
  C -->|"yes"| D{"Program running?"}
  D -->|"no"| Y
  D -->|"yes"| E["Click recorded"]
  E --> F["aff_click cookie<br/>for the cookie window"]
  E --> G["→ brand's site with ?aff_click=…"]
  G --> H["Checkout passes aff_click on:<br/>Stripe client_reference_id,<br/>Shopify landing page, WooCommerce attribution"]
  class E affily
  ${AFFILY}`,
      notes: 'An old post never becomes a dead link: the shopper always lands somewhere useful, and every redirect that records nothing is traced with its reason.',
    },
    {
      title: 'Ways a brand reports sales',
      chart: `flowchart LR
  subgraph OneClick["One click (best)"]
    S1["Stripe Connect<br/>brand approves on Stripe"]
    S2["Shopify app<br/>brand installs it"]
    S3["WooCommerce<br/>brand approves on its store"]
  end
  subgraph Manual["Set up by hand"]
    M1["Stripe or Shopify webhook<br/>brand pastes the signing secret"]
    M2["Server postback<br/>HMAC-signed"]
    M3["Tracking tag<br/>last resort, blockable"]
  end
  OneClick --> R["Order arrives"]
  Manual --> R
  R --> I{"Same order id<br/>seen before?"}
  I -->|"yes"| Dup["Duplicate: one commission"]
  I -->|"no"| A["Attribution"]
  class A affily
  ${AFFILY}`,
      notes:
        'One-click needs the platform’s app keys on the server (for Stripe, STRIPE_CONNECT_CLIENT_ID); without them the card falls back to the paste-a-secret form. A store or Stripe account can belong to one brand only.',
    },
    {
      title: 'Which program a connected store’s sale belongs to',
      chart: `flowchart TB
  A["Sale from a connected store"] --> B{"Store attached<br/>to a program?"}
  B -->|"no"| X["Ignored"]
  B -->|"yes"| C{"Creator code on the order<br/>matches a partnership?"}
  C -->|"yes"| P["That partnership's program"]
  C -->|"no"| D{"aff_click from one<br/>of these programs?"}
  D -->|"yes"| P2["The click's program"]
  D -->|"no"| E{"Renewal of a subscription<br/>already recorded?"}
  E -->|"yes"| P3["Where its first payment went"]
  E -->|"no"| F{"Only one program attached?"}
  F -->|"yes"| P4["That program:<br/>recorded, not paid"]
  F -->|"no"| Y["Ignored, with the reason"]`,
      notes:
        'Next change (agreed): credit a customer’s later sales to the creator who first brought them until the program ends, and let the brands of one Affily account share or split Stripe accounts.',
    },
    {
      title: 'Attribution: who gets the credit',
      chart: `flowchart TB
  A["Order or action"] --> R{"Renewal?"}
  R -->|"yes"| L["Whoever the subscription's<br/>first sale was credited to"]
  R -->|"no"| B{"Creator code?"}
  B -->|"yes"| C["That creator<br/>no cookie window"]
  B -->|"no"| D{"aff_click inside<br/>the cookie window?"}
  D -->|"yes"| E["That click's creator"]
  D -->|"no"| F{"Same device on<br/>this program?"}
  F -->|"yes"| G["That creator"]
  F -->|"no"| U["Unattributed:<br/>counted, not paid"]
  M["Brand-recorded sale"] --> N["Creator of the action it followed,<br/>however long after"]`,
      notes:
        'Unattributed sales are kept rather than dropped so conversion rates are honest. Each commission is paid at the terms frozen when the creator joined.',
    },
  ],
};

const socials = {
  key: 'creators',
  title: 'Creators & socials',
  intro:
    'How a creator gets from signing up to applying, and where verified numbers come from. Typed numbers are always labelled self-reported.',
  charts: [
    {
      title: 'Signing up to applying',
      chart: `flowchart TB
  A["Signs up as a creator"] --> B["Connect your socials<br/>TikTok, Instagram, YouTube,<br/>Facebook Page"]
  B --> C["Build my profile"]
  B --> D["Browse programs first"]
  B --> E["Finish later"]
  D --> F{"Applies to a program"}
  C --> F
  E --> F
  F -->|"no account connected"| G["Connect an account to apply<br/>message kept, then back here"]
  G --> B
  F -->|"connected"| H["Application sent"]
  I["Brand invites the creator"] --> J{"Creator accepts"}
  J -->|"no account connected"| G
  J -->|"connected"| K["Partnership starts"]`,
      notes:
        'The rule is SOCIAL_REQUIRED on the server. It is skipped while no platform can be connected on that server, and partnerships that already existed carry on.',
    },
    {
      title: 'Where verified numbers come from',
      chart: `flowchart LR
  A["Creator connects an account"] --> B["Affily reads followers,<br/>average views and engagement<br/>from the last 12 videos"]
  B --> C["Profile row marked verified<br/>replaces the typed one,<br/>can't be edited by hand"]
  C --> D["Re-read daily"]
  D -->|"read fails"| E["Account: needs reconnecting"]
  C --> F["Creator disconnects"] --> G["Row back to self-reported"]
  class B,C,D affily
  ${AFFILY}`,
      notes:
        'A Facebook Page gives followers only, and needs FACEBOOK_PAGES=on (its own Meta app via FACEBOOK_PAGES_APP_ID, or the sign-in one) plus Meta’s review. TikTok follower counts need its user.info.stats scope.',
    },
  ],
};

const agents = {
  key: 'agents',
  title: 'Agents',
  intro:
    'What an AI agent can do with Affily, and what stays with the person. The instructions agents read are at /agents.md, kept in step with the API.',
  charts: [
    {
      title: 'An agent onboarding someone',
      chart: `flowchart TB
  A["Agent reads /agents.md"] --> B["POST /api/v1/signup<br/>key shown once"]
  B --> C["Uploads assets"]
  C --> D["Builds the profile:<br/>draft, numbers self-reported"]
  D --> E["GET /api/v1/onboarding:<br/>what's left"]
  E --> F["Tells the person"]
  F --> H1["Person connects a social"]
  F --> H2["Person confirms email<br/>and publishes"]
  F --> H3["Person applies"]
  class H1,H2,H3 later
  ${LATER}`,
      notes: 'Dashed steps need the person, signed in, in a browser.',
    },
    {
      title: 'Where a key works',
      chart: `flowchart LR
  K["Request with an affily_sk_ key"] --> P{"Path under /api/v1?"}
  P -->|"yes"| S{"Has the scope?<br/>profile:write, uploads:write"}
  S -->|"yes"| OK["Allowed"]
  S -->|"no"| F403["403"]
  P -->|"no"| Ign["Key ignored:<br/>public reads still work,<br/>anything signed-in is 401"]`,
      notes:
        'A key can never change a password, grant a role, mint another key, publish, connect a social account, apply, or mark numbers verified.',
    },
  ],
};

export const DIAGRAM_SECTIONS = [systemMap, programFlows, connections, socials, agents];
